// ============================================================================
// CrewAhead Maritime Platform - Axios API Client
// ============================================================================

import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE_URL as API_BASE_URL_CFG, API_BASE_PATH } from '../api-config';
import { STORAGE_KEYS } from '../utils/constants';
import { camelCaseKeys, snakeCaseKeys } from '../utils/transforms';
import type { ApiError } from '../types/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  retryCount?: number;
}

/**
 * Custom API error class with typed fields.
 *
 * Use the {@link handleApiError} factory to construct from unknown catch values.
 */
export class CrewAheadApiError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly details: Record<string, string[]> | null;
  public readonly timestamp: string;
  public readonly originalError: AxiosError | null;

  constructor(params: {
    message: string;
    statusCode: number;
    code: string;
    details: Record<string, string[]> | null;
    timestamp: string;
    originalError?: AxiosError | null;
  }) {
    super(params.message);
    this.statusCode = params.statusCode;
    this.code = params.code;
    this.details = params.details;
    this.timestamp = params.timestamp;
    this.originalError = params.originalError ?? null;
    this.name = 'CrewAheadApiError';

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CrewAheadApiError);
    }
  }
}

/**
 * Extracts a human-readable message and field details from a Django REST
 * Framework error payload. DRF returns either `{ detail: string }`,
 * `{ field_name: ["message"] }`, or `{ non_field_errors: ["message"] }.
 */
function parseDrfError(
  data: unknown
): { message: string | null; details: Record<string, string[]> | null } {
  if (!data || typeof data !== 'object') {
    return { message: null, details: null };
  }

  const record = data as Record<string, unknown>;

  if (typeof record.detail === 'string') {
    return { message: record.detail, details: null };
  }

  const details: Record<string, string[]> = {};
  let firstMessage: string | null = null;

  for (const [key, value] of Object.entries(record)) {
    if (Array.isArray(value)) {
      const messages = value.filter((v): v is string => typeof v === 'string');
      if (messages.length > 0) {
        details[key] = messages;
        if (!firstMessage) {
          firstMessage = messages[0];
        }
      }
    } else if (typeof value === 'string') {
      details[key] = [value];
      if (!firstMessage) {
        firstMessage = value;
      }
    }
  }

  return {
    message: firstMessage,
    details: Object.keys(details).length > 0 ? details : null,
  };
}

/**
 * Safely constructs a CrewAheadApiError from an unknown catch value.
 * Use this in every catch block instead of `new CrewAheadApiError(...)`.
 */
export function handleApiError(error: unknown): CrewAheadApiError {
  // Already a CrewAheadApiError — pass through
  if (error instanceof CrewAheadApiError) {
    return error;
  }

  // Axios error with response
  if (axios.isAxiosError(error)) {
    const axiosErr = error as AxiosError;
    const responseData = axiosErr.response?.data;
    const apiError = responseData as ApiError | undefined;
    const drf = parseDrfError(responseData);

    return new CrewAheadApiError({
      message:
        apiError?.message ??
        drf.message ??
        axiosErr.message ??
        'An unexpected error occurred',
      statusCode:
        apiError?.statusCode ??
        axiosErr.response?.status ??
        500,
      code: apiError?.code ?? 'UNKNOWN_ERROR',
      details: apiError?.details ?? drf.details ?? null,
      timestamp: apiError?.timestamp ?? new Date().toISOString(),
      originalError: axiosErr,
    });
  }

  // Plain Error
  if (error instanceof Error) {
    return new CrewAheadApiError({
      message: error.message,
      statusCode: 500,
      code: 'UNKNOWN_ERROR',
      details: null,
      timestamp: new Date().toISOString(),
    });
  }

  // Fallback for unknown values
  return new CrewAheadApiError({
    message: typeof error === 'string' ? error : 'An unexpected error occurred',
    statusCode: 500,
    code: 'UNKNOWN_ERROR',
    details: null,
    timestamp: new Date().toISOString(),
  });
}

// ─── Token Management ────────────────────────────────────────────────────────

/**
 * Retrieves the stored access token.
 */
function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      console.info('[Auth] No access token found in localStorage');
    }
    return token;
  } catch {
    return null;
  }
}

/**
 * Build the access-token cookie string with production domain scoping.
 *
 * - On localhost/127.0.0.1 we omit Domain and Secure so HTTP dev works.
 * - On crewahead.com hosts we set Domain=.crewahead.com so the cookie is
 *   shared between www and the bare domain, and Secure when served over HTTPS.
 */
function buildAccessTokenCookie(value: string, maxAge: number): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const domainPart = isLocalhost ? '' : ';domain=.crewahead.com';
  const securePart = !isLocalhost && window.location.protocol === 'https:' ? ';Secure' : '';
  return `ca_at=${value};path=/;max-age=${maxAge};SameSite=Lax${domainPart}${securePart}`;
}

/**
 * Stores authentication tokens in localStorage.
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    // Sync access token to cookie so Next.js middleware can read it
    document.cookie = buildAccessTokenCookie(accessToken, 604800); // 7 days
    console.info('[Auth] Tokens saved to localStorage + cookie');
  } catch (error) {
    console.error('[Auth] Failed to save tokens:', error);
  }
}

/**
 * Clears all authentication data from localStorage.
 */
export function clearAuth(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.LAST_ROUTE);
    // Clear cookie so middleware knows user is logged out
    document.cookie = buildAccessTokenCookie('', 0);
  } catch (error) {
    console.error('Failed to clear auth:', error);
  }
}

/**
 * Checks if the current route is the login page to avoid redirect loops.
 */
function isLoginPage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/login';
}

// ─── Axios Instance ──────────────────────────────────────────────────────────

/**
 * Base URL for the API.
 * - Browser: uses relative '/api/v1' → routes through Next.js proxy (avoids CORS)
 * - Server (SSR): uses full backend URL directly
 *
 * Sources from centralized api-config.ts for single source of truth.
 */
const API_BASE_URL =
  typeof window === 'undefined'
    ? `${API_BASE_URL_CFG}${API_BASE_PATH}` // server-side: full URL
    : API_BASE_PATH; // browser → Next.js proxy rewrite

/**
 * Request timeout in milliseconds.
 */
const API_TIMEOUT = Number(process.env.NEXT_PUBLIC_API_TIMEOUT ?? 30000);

/**
 * Global axios instance configured for the CrewAhead API.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Request Interceptor ─────────────────────────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const extendedConfig = config as ExtendedAxiosRequestConfig;
    const url = extendedConfig.url ?? '';

    // Skip auth header for requests marked with skipAuth
    if (!extendedConfig.skipAuth) {
      const token = getAccessToken();
      if (token) {
        extendedConfig.headers.set('Authorization', `Bearer ${token}`);
        console.info(`[Auth] Attached token to ${extendedConfig.method?.toUpperCase()} ${url}`);
      } else {
        console.info(`[Auth] No token for ${extendedConfig.method?.toUpperCase()} ${url}`);
      }
    }

    // Transform request body from camelCase → snake_case for Django backend
    if (extendedConfig.data && typeof extendedConfig.data === 'object') {
      // Skip FormData and Blob objects
      if (
        !(extendedConfig.data instanceof FormData) &&
        !(extendedConfig.data instanceof Blob) &&
        !(extendedConfig.data instanceof ArrayBuffer)
      ) {
        extendedConfig.data = snakeCaseKeys(extendedConfig.data);
      } else if (extendedConfig.data instanceof FormData) {
        // Drop the default application/json header so the browser generates the
        // correct multipart/form-data boundary for the file upload.
        extendedConfig.headers.delete('Content-Type');
      }
    }

    // Add request timestamp for debugging
    extendedConfig.headers.set('X-Request-Time', new Date().toISOString());

    // Add correlation ID for tracing
    const correlationId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    extendedConfig.headers.set('X-Correlation-Id', correlationId);

    return extendedConfig;
  },
  (error: AxiosError): Promise<never> => {
    return Promise.reject(error);
  }
);

// ─── Response Interceptor ────────────────────────────────────────────────────

/**
 * Flag to prevent multiple concurrent refresh attempts.
 */
let isRefreshing = false;

/**
 * Queue of failed requests waiting for token refresh.
 */
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Subscribes a callback to be called when token refresh completes.
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notifies all subscribers that token refresh completed with the new token.
 */
function onTokenRefreshed(newToken: string): void {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  // Success handler: unwrap ApiResponse envelope + transform snake_case → camelCase
  (response: AxiosResponse): AxiosResponse => {
    // If the response data has our standard ApiResponse shape, unwrap it
    if (
      response.data &&
      typeof response.data === 'object' &&
      'success' in response.data &&
      'data' in response.data
    ) {
      // Replace response data with just the inner data
      // Keep the full response accessible via response.config for raw access
      (response as unknown as Record<string, unknown>).originalData = response.data;
      response.data = response.data.data;
    }

    // Transform snake_case → camelCase for all object/array responses
    // Django REST Framework returns snake_case field names but our
    // TypeScript types use camelCase. This ensures runtime data matches types.
    if (
      response.data &&
      typeof response.data === 'object' &&
      !(response.data instanceof Blob) &&
      !(response.data instanceof ArrayBuffer)
    ) {
      response.data = camelCaseKeys<typeof response.data>(response.data);
    }

    return response;
  },

  // Error handler
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(handleApiError(error));
    }

    const status = error.response?.status;

    // Handle 401 Unauthorized
    if (status === 401) {
      // Skip auth endpoints — 401 here means wrong credentials, not expired token
      const url = originalRequest.url ?? '';
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/token/refresh') ||
        url.includes('/auth/social/') ||
        url.includes('/auth/password/') ||
        url.includes('/auth/email/');
      if (isAuthEndpoint) {
        return Promise.reject(handleApiError(error));
      }

      // If this is already a retry after refresh, clear auth and redirect
      if (originalRequest.retryCount && originalRequest.retryCount > 0) {
        clearAuth();
        if (!isLoginPage()) {
          // Store current route for post-login redirect
          try {
            localStorage.setItem(
              STORAGE_KEYS.LAST_ROUTE,
              window.location.pathname + window.location.search
            );
          } catch {
            // ignore storage errors
          }
          window.location.href = '/login';
        }
        return Promise.reject(handleApiError(error));
      }

      // Attempt token refresh
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response = await axios.post<Record<string, unknown>>(
            `${API_BASE_URL}/auth/token/refresh`,
            { refresh: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          // Handle both wrapped and unwrapped responses
          // Since this uses plain axios (not apiClient), response interceptors
          // don't run — we get raw backend response keys (access_token/access, refresh_token/refresh)
          const rawData = response.data;
          const accessToken = (rawData.access_token ?? rawData.access ?? '') as string;
          const newRefreshToken = (rawData.refresh_token ?? rawData.refresh) as
            | string
            | undefined;

          if (!accessToken) {
            throw new Error('Invalid refresh token response');
          }

          // A 200 refresh response may legitimately omit the refresh token
          // (e.g. ROTATE_REFRESH_TOKENS=False). Never overwrite the stored
          // refresh token with an empty value — retain the existing one.
          if (!newRefreshToken) {
            console.warn(
              '[Auth] Refresh response omitted a refresh token — retaining existing stored refresh token'
            );
          }

          setTokens(accessToken, newRefreshToken ?? refreshToken);
          onTokenRefreshed(accessToken);
          isRefreshing = false;

          // Retry the original request with new token
          originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
          originalRequest.retryCount = (originalRequest.retryCount ?? 0) + 1;
          return apiClient(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          clearAuth();
          console.error('[Auth] Token refresh failed:', refreshError);

          if (!isLoginPage()) {
            try {
              localStorage.setItem(
                STORAGE_KEYS.LAST_ROUTE,
                window.location.pathname + window.location.search
              );
            } catch {
              // ignore
            }
            window.location.href = '/login';
          }

          return Promise.reject(handleApiError(error));
        }
      }

      // If refresh is already in progress, queue this request
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken: string) => {
          originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
          originalRequest.retryCount = (originalRequest.retryCount ?? 0) + 1;
          resolve(apiClient(originalRequest));
        });
      });
    }

    // Handle 403 Forbidden
    if (status === 403) {
      // User doesn't have permission - could redirect to access denied page
      console.warn('Access forbidden:', originalRequest.url);
    }

    // Handle 429 Rate Limited
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'];
      console.warn(`Rate limited. Retry after: ${retryAfter ?? 'unknown'} seconds`);
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - no response received:', error.message);
    }

    return Promise.reject(handleApiError(error));
  }
);

// ─── Generic API Call ────────────────────────────────────────────────────────

/**
 * Performs a typed API request. Returns the raw response data.
 * The response interceptor handles both camelCase transformation and
 * ApiResponse envelope unwrapping automatically.
 */
export async function apiCall<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url' | 'data'>
): Promise<T> {
  const response = await apiClient.request<T>({
    method,
    url,
    data,
    ...config,
  });
  return response.data;
}

// ─── Helper Exports ──────────────────────────────────────────────────────────

/**
 * Performs a GET request and returns typed data.
 */
export async function get<T>(
  url: string,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url'>
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

/**
 * Performs a POST request and returns typed response data.
 */
export async function post<T>(
  url: string,
  data?: unknown,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url' | 'data'>
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

/**
 * Performs a PUT request and returns typed response data.
 */
export async function put<T>(
  url: string,
  data?: unknown,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url' | 'data'>
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

/**
 * Performs a PATCH request and returns typed response data.
 */
export async function patch<T>(
  url: string,
  data?: unknown,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url' | 'data'>
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

/**
 * Performs a DELETE request and returns typed response data.
 */
export async function del<T>(
  url: string,
  config?: Omit<ExtendedAxiosRequestConfig, 'method' | 'url'>
): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}